/** Port Performance Database — table filter cascade helpers. */

import {
  matchesDatabaseOperationFilter,
  normalizeOperationsFilterOptions,
} from './port-database-filter-api.util';
import {
  extractClientOptionsFromRecords,
  extractSegmentOptionsFromRecords,
  matchSegmentFilter,
  matchesAnyClient,
  matchesAnySegment,
  matchesSelectedPorts,
  matchesSelectedVesselNames,
} from './pc-filter-options.util';

export type TableFilterCascadeLevel =
  | 'base'
  | 'client'
  | 'segment'
  | 'operations'
  | 'vessel'
  | 'port';

export interface TableFilterCascadeState {
  selectedClients: string[];
  selectedSegments: string[];
  selectedOperations: string[];
  selectedShips: string[];
  selectedPorts: string[];
}

export function normalizeFilterOptionKey(value: string): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Case-insensitive dedupe; keeps the first readable display value. */
export function dedupeFilterOptions(values: string[]): string[] {
  const byKey = new Map<string, string>();

  for (const raw of values || []) {
    const trimmed = String(raw ?? '').replace(/\s+/g, ' ').trim();
    if (!trimmed) {
      continue;
    }
    const key = normalizeFilterOptionKey(trimmed);
    if (!byKey.has(key)) {
      byKey.set(key, trimmed);
    }
  }

  return Array.from(byKey.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

function matchesSelectedOperations(
  itemOperation: string | null | undefined,
  selectedOperations: string[],
): boolean {
  return matchesDatabaseOperationFilter(itemOperation, selectedOperations);
}

export function scopeTableFilterRecords(
  records: any[],
  state: TableFilterCascadeState,
  through: TableFilterCascadeLevel,
): any[] {
  let scoped = [...(records || [])];

  if (through === 'base') {
    return scoped;
  }

  if (state.selectedClients.length) {
    scoped = scoped.filter((item) =>
      matchesAnyClient(item?.client, state.selectedClients),
    );
  }
  if (through === 'client') {
    return scoped;
  }

  if (state.selectedSegments.length) {
    scoped = scoped.filter((item) =>
      matchesAnySegment(item?.segment, state.selectedSegments),
    );
  }
  if (through === 'segment') {
    return scoped;
  }

  if (state.selectedOperations.length) {
    scoped = scoped.filter((item) =>
      matchesSelectedOperations(item?.operations, state.selectedOperations),
    );
  }
  if (through === 'operations') {
    return scoped;
  }

  if (state.selectedShips.length) {
    scoped = scoped.filter((item) =>
      matchesSelectedVesselNames(item?.vessel_name, state.selectedShips),
    );
  }
  if (through === 'vessel') {
    return scoped;
  }

  if (state.selectedPorts.length) {
    scoped = scoped.filter((item) =>
      matchesSelectedPorts(item?.port, state.selectedPorts),
    );
  }

  return scoped;
}

export function buildTableFilterCascadeOptions(
  records: any[],
  state: TableFilterCascadeState,
): {
  clientOptions: string[];
  segmentOptions: string[];
  operationOptions: string[];
  vesselOptions: string[];
  portOptions: string[];
} {
  const clientOptions = extractClientOptionsFromRecords(records);

  const segmentOptions = extractSegmentOptionsFromRecords(
    scopeTableFilterRecords(records, state, 'client'),
  );

  const operationOptions = normalizeOperationsFilterOptions(
    scopeTableFilterRecords(records, state, 'segment')
      .map((record) => record?.operations)
      .filter(Boolean),
  );

  const vesselOptions = dedupeFilterOptions(
    scopeTableFilterRecords(records, state, 'operations')
      .map((record) => record?.vessel_name)
      .filter(Boolean),
  );

  const portOptions = dedupeFilterOptions(
    scopeTableFilterRecords(records, state, 'vessel')
      .map((record) => record?.port)
      .filter(Boolean),
  );

  return {
    clientOptions,
    segmentOptions,
    operationOptions,
    vesselOptions,
    portOptions,
  };
}

export function pruneTableFilterSelections(
  state: TableFilterCascadeState,
  options: ReturnType<typeof buildTableFilterCascadeOptions>,
): TableFilterCascadeState {
  const clientKeys = new Set(
    options.clientOptions.map((value) => normalizeFilterOptionKey(value)),
  );
  const operationKeys = new Set(
    options.operationOptions.map((value) => normalizeFilterOptionKey(value)),
  );
  const vesselKeys = new Set(
    options.vesselOptions.map((value) => normalizeFilterOptionKey(value)),
  );
  const portKeys = new Set(
    options.portOptions.map((value) => normalizeFilterOptionKey(value)),
  );

  return {
    selectedClients: state.selectedClients.filter((value) =>
      clientKeys.has(normalizeFilterOptionKey(value)),
    ),
    selectedSegments: state.selectedSegments.filter((value) =>
      options.segmentOptions.some((opt) => matchSegmentFilter(opt, value)),
    ),
    selectedOperations: state.selectedOperations.filter((value) =>
      operationKeys.has(normalizeFilterOptionKey(value)),
    ),
    selectedShips: state.selectedShips.filter((value) =>
      vesselKeys.has(normalizeFilterOptionKey(value)),
    ),
    selectedPorts: state.selectedPorts.filter((value) =>
      portKeys.has(normalizeFilterOptionKey(value)),
    ),
  };
}

/** PC benchmark cascade level — Client → Segment → Operations → Vessel → Port → Yard */
export type PcBenchmarkCascadeLevel =
  | 'client'
  | 'segment'
  | 'operations'
  | 'vessel'
  | 'port'
  | 'yard'
  | 'chart';

export function extractRecordOperation(record: any): string {
  return String(record?.operation || record?.operations || '').trim();
}

export function extractRecordYard(
  record: any,
  vesselMeta?: { ship_yard?: string } | null,
): string {
  return String(
    record?.yard ||
      record?.yard_name ||
      record?.ship_yard ||
      vesselMeta?.ship_yard ||
      '',
  ).trim();
}

/** Parse API `build_year` (date or year) into vessel age in whole years. */
export function getVesselAgeYearsFromBuildDate(
  buildYear: string | number | null | undefined,
  now: Date = new Date(),
): number | null {
  if (buildYear === null || buildYear === undefined) {
    return null;
  }
  const raw = String(buildYear).trim();
  if (!raw) {
    return null;
  }

  const built = new Date(raw);
  if (!Number.isNaN(built.getTime())) {
    let age = now.getFullYear() - built.getFullYear();
    const monthDiff = now.getMonth() - built.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && now.getDate() < built.getDate())
    ) {
      age -= 1;
    }
    return Math.max(0, age);
  }

  const yearOnly = Number.parseInt(raw, 10);
  if (!Number.isFinite(yearOnly) || yearOnly < 1900 || yearOnly > now.getFullYear()) {
    return null;
  }
  return Math.max(0, now.getFullYear() - yearOnly);
}

export function extractRecordBuildYear(record: any): string {
  return String(
    record?.ship_built_to ||
      record?.ship_built_from ||
      record?.build_year ||
      record?.built_year ||
      record?.ship_built ||
      record?.ship_built_date ||
      record?.date_built ||
      record?.year_built ||
      '',
  ).trim();
}

export function formatVesselAgeLabel(ageYears: number): string {
  return ageYears === 1 ? '1 year' : `${ageYears} years`;
}
